// TEN11-S5 (docs/build-briefs/TEN11-S5-customer-sees-support.md). The Support access tab of the Audit log: the
// sessions a Software Owner opened at one location, newest first, each with who, started, ended and the changes made
// during it. Read only: it reads one route and writes nothing. Text is set with textContent only.
//
// States: loading, the list, N/A (no session), error with Try again, and no access (a 403).

import { getSupportSessions, type SupportSessionDto, type SupportSessionsDto } from "./api.ts";
import type { AuditLogFilters } from "./auditLogModel.ts";
import type { Session } from "./session.ts";

export interface SupportSessionsOptions {
  /** Opens the Activity tab filtered to one support person and day range. */
  openActivity: (filters: Partial<AuditLogFilters>) => void;
  /** TEN11-F2: ends a live franchisor session, then the caller reloads the list. */
  endNow: (sessionId: string) => Promise<void>;
}

let generation = 0;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  testId?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== "") element.className = className;
  if (testId !== undefined) element.dataset["testid"] = testId;
  if (text !== undefined) element.textContent = text;
  return element;
}

export function whenLabel(iso: string | null, timeZone: string): string {
  if (iso === null) return "-";
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

const END_REASON_LABELS: Record<string, string> = {
  ended_by_support: "Ended by support",
  ended_by_location: "Ended by you",
  signed_out: "Signed out",
  idle: "Idle",
  disconnected: "Disconnected",
  idle_expired: "Idle",
  platform_session_ended: "Signed out",
  expired: "Expired",
  capped: "Replaced",
  account_disabled: "Account disabled",
  tenant_closed: "Location closed",
  franchisor_session_ended: "Signed out",
  permission_removed: "Access removed",
  network_inactive: "Access removed",
  left_network: "Left the network",
  end_requested: "Ended",
};

export function endLabel(session: SupportSessionDto): string {
  if (session.endedAt === null) return "Live";
  return session.endReason === null ? "Ended" : (END_REASON_LABELS[session.endReason] ?? "Ended");
}

function renderSession(
  dto: SupportSessionsDto,
  session: SupportSessionDto,
  options: SupportSessionsOptions,
): HTMLElement {
  const tz = dto.timeZone ?? "UTC";
  const card = el("div", "ch-card al-support-row", "support-session-row");
  card.dataset["sessionId"] = session.sessionId;
  const head = el("div", "al-support-head");
  head.append(
    el("b", "", "support-session-who", session.who),
    el("span", "", "support-session-started", `Started ${whenLabel(session.startedAt, tz)}`),
    el(
      "span",
      "",
      "support-session-ended",
      session.endedAt === null ? "Live" : `Ended ${whenLabel(session.endedAt, tz)}`,
    ),
    el("span", "ch-tag", "support-session-reason", endLabel(session)),
    el(
      "span",
      "",
      "support-session-count",
      `${session.changeCount} ${session.changeCount === 1 ? "change" : "changes"}`,
    ),
  );
  card.append(head);
  if (session.changes.length > 0) {
    const list = el("ul", "al-support-changes", "support-session-changes");
    for (const change of session.changes) {
      list.append(
        el(
          "li",
          "",
          "support-session-change",
          `${whenLabel(change.at, tz)}  ${change.routeKind}${change.subjectKind === null ? "" : `  ${change.subjectKind}`}`,
        ),
      );
    }
    card.append(list);
  }
  const open = el("button", "ch-btn-secondary", "support-session-open-activity", "View in Activity");
  open.type = "button";
  open.addEventListener("click", () =>
    options.openActivity({
      range: "custom",
      from: session.fromDay,
      to: session.toDay,
      person: session.supportUserId,
      locationId: dto.locationId ?? "",
    }),
  );
  card.append(open);
  // TEN11-F2: a live session a franchisor opened can be ended by the location's Admin. The server decides who may.
  if (session.endedAt === null && session.byFranchisor) {
    const endNow = el("button", "ch-btn-secondary", "support-session-end-now", "End now");
    endNow.type = "button";
    endNow.addEventListener("click", () => {
      endNow.disabled = true;
      void options.endNow(session.sessionId);
    });
    card.append(endNow);
  }
  return card;
}

export async function openSupportSessions(
  session: Session,
  container: HTMLElement,
  options: SupportSessionsOptions,
  locationId = "",
): Promise<void> {
  generation += 1;
  const mine = generation;
  container.textContent = "";
  const root = el("div", "ch-root al-root", "support-sessions-root");
  container.append(root);
  root.append(el("p", "ch-summary", "support-sessions-loading", "Loading..."));
  const result = await getSupportSessions(session.apiBaseUrl, session.tenantId, locationId);
  if (mine !== generation) return;
  root.textContent = "";
  if (result.status === 403) {
    root.append(el("p", "ch-summary", "support-sessions-no-access", "No access"));
    return;
  }
  const body = result.body;
  if (result.status !== 200 || body.sessions === undefined) {
    const err = el("div", "ch-card", "support-sessions-error");
    err.append(el("p", "", undefined, "Could not load."));
    const retry = el("button", "ch-btn-secondary", "support-sessions-retry", "Try again");
    retry.type = "button";
    retry.addEventListener("click", () => void openSupportSessions(session, container, options, locationId));
    err.append(retry);
    root.append(err);
    return;
  }
  if (body.locations.length > 1) {
    const select = el("select", "ch-sel", "support-sessions-location");
    for (const siteLocation of body.locations) {
      const option = document.createElement("option");
      option.value = siteLocation.id;
      option.textContent = siteLocation.name;
      select.append(option);
    }
    select.value = body.locationId ?? "";
    select.addEventListener("change", () =>
      void openSupportSessions(session, container, options, select.value),
    );
    root.append(select);
  }
  if (body.sessions.length === 0) {
    root.append(el("p", "ch-summary", "support-sessions-empty", "No support sessions yet."));
    return;
  }
  for (const entry of body.sessions) {
    root.append(renderSession(body, entry, options));
  }
}

export function resetSupportSessions(container: HTMLElement): void {
  generation += 1;
  container.textContent = "";
}
